/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormulaContext, FormulaTestEvent } from '@gh/builder-shared';
import { Formula, isFormulaEmpty, isFormulaValid } from '@gh/builder-shared-data';
import { getRouteInitData, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { isSomeTrue } from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';
import { isEmpty } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import {
  getHpeItemId,
  HpeComponent,
  HpeItem,
  isHpeItemFromComponent,
} from '../../../resources/hpe-details.model';
import { HpeCommandService } from '../hpe-command.service';
import {
  HpeDetailsState,
  queryHpeComponent,
  queryHpeEditable,
  queryHpeFormulaHasTopsFromGto,
  queryHpeSelectedDetailsTab,
  queryHpeSelectedItem,
  queryHpeSelectedItemId,
  queryHpeSelectionHighlightInvalid,
} from '../hpe-details.state';

@Component({
  standalone: false,
  selector: 'gh-hpe-details-cc-item-details',
  templateUrl: 'hpe-details-cc-item-details.component.html',
  styleUrls: ['hpe-details-cc-item-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HpeDetailsCcItemDetailsComponent implements OnInit, OnDestroy {
  formulaContext: FormulaContext;

  formula$: Observable<Maybe<Formula>>;
  selectionIdentity$: Observable<Maybe<ID>>;
  memo$: Observable<string>;
  total$: Observable<number>;
  selectedTab$: Observable<string>;
  formulaEditable$: Observable<boolean>;
  highlightInvalid$: Observable<boolean>;
  isComponent$: Observable<boolean>;
  isFreeItem$: Observable<boolean>;
  isFormulaNotPropagatable$: Observable<boolean>;
  itemComponent$: Observable<HpeComponent>;

  editable: boolean;

  private item: HpeItem;
  private itemSubscription: Subscription;

  constructor(private route: ActivatedRoute,
              private state: State<HpeDetailsState>,
              private commands: HpeCommandService) {

  }

  ngOnInit(): void {
    const { formulaContext } = getRouteInitData(this.route);

    this.formulaContext = formulaContext;

    const { state } = this;
    const item$ = state.select(queryHpeSelectedItem).pipe(filter$(Boolean));

    const formula$ = item$.pipe(map$((item: any) => item.formula));
    this.formula$ = formula$;
    this.selectionIdentity$ = state.select(queryHpeSelectedItemId);
    this.memo$ = item$.pipe(map$((item: any) => item.memo));
    this.total$ = item$.pipe(map$((item: any) => item.cost || 0));
    this.selectedTab$ = state.select(queryHpeSelectedDetailsTab).pipe(filter$(Boolean));
    this.editable = state.project(queryHpeEditable);
    this.formulaEditable$ = item$.pipe(map$((item: any) => isEmpty(item.cuttingList)));
    this.highlightInvalid$ = state.select(queryHpeSelectionHighlightInvalid);
    this.isComponent$ = item$.pipe(map$((item: any) => item && isHpeItemFromComponent(item) || false));
    this.isFreeItem$ = item$.pipe(map$((item: any) => item && !isHpeItemFromComponent(item) || false));
    this.itemComponent$ = item$.pipe(switchMap$((item: any) =>
      <Observable<HpeComponent>>this.state.select(queryHpeComponent, <ID>item.hpComponentId)));

    const isFormulaEmpty$ = formula$.pipe(map$(isFormulaEmpty));
    const isFormulaNotValid$ = formula$.pipe(
      map$((formula) => isFormulaEmpty(formula) ? false : !isFormulaValid(formula)));
    const isFormulaHasTopsFromGto$ = formula$.pipe(map$((formula) => isFormulaEmpty(formula)
      ? false
      : this.state.project(queryHpeFormulaHasTopsFromGto, formula)));
    this.isFormulaNotPropagatable$ = combineLatest$(
      isFormulaEmpty$,
      isFormulaNotValid$,
      isFormulaHasTopsFromGto$).pipe(map$(isSomeTrue));

    this.itemSubscription = item$.subscribe((item: any) => {
      this.item = item;
    });
  }

  ngOnDestroy(): void {
    this.itemSubscription.unsubscribe();
  }

  onTest(event: FormulaTestEvent): void {
    this.commands.testItemFormula(event, getHpeItemId(this.item));
  }

  onFormulaChange(formula: Formula): void {
    const { item } = this;

    if (item) {
      this.commands.costCenterItemSetField(getHpeItemId(item), 'formula', formula);
    }
  }

  onMemoChange(memo: string): void {
    const { item } = this;
    if (item) {
      this.commands.costCenterItemSetField(getHpeItemId(item), 'memo', memo);
    }
  }

  onSelectedTabChange(name: string): void {
    this.commands.tabSelect(name);
  }

  goToComponent(): void {
    const { item } = this;

    if (item && item.hpComponentId) {
      this.commands.componentSelect(item.hpComponentId);
    }
  }

  showPropagateFormulaPopup(): void {
    this.commands.propagateItemFormula(this.item);
  }
}
